import {
    AlertTriangle,
    CheckCircle,
    Clock,
    Eye,
    Search,
    Send
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const OverdueManagement = () => {
  const { user } = useAuth();
  const [overdueList, setOverdueList] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOverdue, setSelectedOverdue] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);

  // 템플릿 관련 헬퍼 함수들
  const getRecommendedTemplateTitle = (stage) => {
    const titles = {
      DUE_REMINDER: '연체 1단계 납부안내',
      D3: '연체 2단계 1차독촉',
      D15: '연체 3단계 2차독촉',
      D30: '연체 4단계 연체료부과',
      D60: '연체 5단계 내용증명',
      D90: '연체 6단계 최종통지',
      D100: '연체 7단계 법무단계'
    };
    return titles[stage] || '기본 안내 메시지';
  };

  const getRecommendedTemplateDescription = (stage) => {
    const descriptions = {
      DUE_REMINDER: '납부기한 확인과 납부 예정 안내',
      D3: '3일 이내 초기 연체를 부드럽게 안내',
      D15: '15일 이내 연체 지속 상황을 정중하게 독촉',
      D30: '30일 이내 연체료 부과 가능성을 안내',
      D60: '60일 이내 내용증명 등 추가 절차를 사전 안내',
      D90: '90일 이내 계약 해지 및 법적 절차 전 최종 안내',
      D100: '90일 초과 법무 검토 단계 이관 안내'
    };
    return descriptions[stage] || '기본 연체 안내 메시지';
  };

  const replaceTemplateVariables = (content, overdue) => {
    if (!content || !overdue) return content;
    
    let preview = content;
    preview = preview.replace(/{tenant_name}/g, overdue.tenant_name || '');
    preview = preview.replace(/{building_name}/g, overdue.building_name || '');
    preview = preview.replace(/{unit_number}/g, overdue.unit_number || '');
    preview = preview.replace(/{property_owner_name}/g, overdue.property_owner_name || '');
    preview = preview.replace(/{company_name}/g, overdue.company_name || overdue.property_owner_name || '');
    preview = preview.replace(/{overdue_amount}/g, overdue.overdue_amount?.toLocaleString() || '0');
    preview = preview.replace(/{overdue_days}/g, overdue.overdue_days || '0');
    preview = preview.replace(/{late_fee_amount}/g, overdue.late_fee_amount?.toLocaleString() || '0');
    preview = preview.replace(/{total_amount}/g, ((overdue.overdue_amount || 0) + (overdue.late_fee_amount || 0))?.toLocaleString() || '0');
    preview = preview.replace(/{due_date}/g, overdue.due_date || '');
    preview = preview.replace(/{company_phone}/g, overdue.contact_phone || '');
    preview = preview.replace(/{contact_phone}/g, overdue.contact_phone || '');
    preview = preview.replace(/{contact_email}/g, overdue.contact_email || '');
    
    return preview;
  };
  
  // 필터 상태
  const [filters, setFilters] = useState({
    building_id: '',
    status: '',
    stage: '',
    search: '',
    property_owner_id: ''
  });
  
  // 알림 모달 상태
  const [notificationData, setNotificationData] = useState({
    notification_type: 'SMS',
    custom_message: '',
    selected_template: '',
    use_template: true
  });
  
  // 템플릿 상태
  const [templates, setTemplates] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // 해결 모달 상태
  const [resolveData, setResolveData] = useState({
    resolution_method: '',
    notes: ''
  });

  // 연체 상태별 색상
  const statusColors = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    brown: 'bg-amber-100 text-amber-800',
    dark_brown: 'bg-orange-100 text-orange-800',
    pink: 'bg-pink-100 text-pink-800',
    red: 'bg-red-100 text-red-800',
    black: 'bg-gray-800 text-white'
  };

  // 연체 단계별 한글명
  const stageNames = {
    DUE_REMINDER: '납부안내',
    D3: '1차독촉',
    D15: '2차독촉',
    D30: '연체료부과',
    D60: '내용증명',
    D90: '최종통지',
    D100: '법무단계'
  };

  // 템플릿 가져오기
  const fetchTemplates = async () => {
    try {
      console.log('🔍 템플릿 로딩 시작');
      setLoadingTemplates(true);
      const response = await api.get('/overdue/templates');
      console.log('🔍 템플릿 API 응답:', response);
      console.log('🔍 템플릿 API 응답 타입:', typeof response);
      console.log('🔍 템플릿 API 응답 키들:', Object.keys(response || {}));
      
      // API 응답 구조에 맞게 데이터 추출
      // api.js 인터셉터가 response.data를 반환하므로, response는 {success: true, data: {...}} 형태
      let templatesData = {};
      
      if (response) {
        if (response.success && response.data) {
          // 표준 응답 구조: {success: true, data: {...}}
          templatesData = response.data || {};
          console.log('🔍 표준 응답 구조로 템플릿 데이터 추출');
        } else if (response.data && typeof response.data === 'object') {
          // data 필드가 객체인 경우
          templatesData = response.data;
          console.log('🔍 data 필드로 템플릿 데이터 추출');
        } else if (typeof response === 'object' && !Array.isArray(response)) {
          // 객체가 직접 반환된 경우 (data 필드 없이)
          templatesData = response;
          console.log('🔍 객체 직접 반환으로 템플릿 데이터 추출');
        }
      }
      
      console.log('🔍 템플릿 데이터 최종:', templatesData);
      console.log('🔍 템플릿 단계들:', Object.keys(templatesData));
      setTemplates(templatesData);
    } catch (error) {
      console.error('❌ 템플릿 조회 실패:', error);
      console.error('❌ 오류 상세:', error.response?.data);
      setTemplates({});
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchOverdueList();
    fetchBuildings();
    if (user?.role === 'rental_management_company' || 
        user?.role === 'rmc_admin' || 
        user?.role === 'rmc_staff' ||
        (user?.user_type === 'rmc_user' && user?.role === 'admin')) {
      fetchPropertyOwners();
    }
    fetchTemplates();
  }, [filters]);

  const fetchOverdueList = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.building_id) params.append('building_id', filters.building_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.property_owner_id) params.append('property_owner_id', filters.property_owner_id);
      
      console.log('연체관리 API 호출 시작:', `/overdue/list?${params.toString()}`);
      const response = await api.get(`/overdue/list?${params.toString()}`);
      console.log('연체관리 API 응답:', response);
      console.log('연체관리 API 응답 타입:', typeof response);
      console.log('연체관리 API 응답 키들:', Object.keys(response || {}));
      console.log('연체관리 API 응답 전체 내용:', JSON.stringify(response, null, 2));
      
      // API 응답 구조에 맞게 데이터 추출
      // api.js 인터셉터가 response.data를 반환하므로, response는 {success: true, data: [...]} 형태
      let overdueData = [];
      
      if (response) {
        console.log('response.success:', response.success);
        console.log('response.data:', response.data);
        console.log('response.data 타입:', typeof response.data);
        console.log('response.data가 배열인가?', Array.isArray(response.data));
        
        if (response.success && response.data) {
          // 표준 응답 구조: {success: true, data: [...]}
          overdueData = Array.isArray(response.data) ? response.data : [];
          console.log('표준 응답 구조로 데이터 추출:', overdueData.length, '건');
        } else if (Array.isArray(response)) {
          // 배열이 직접 반환된 경우
          overdueData = response;
          console.log('배열 직접 반환으로 데이터 추출:', overdueData.length, '건');
        } else if (response.data && Array.isArray(response.data)) {
          // data 필드가 배열인 경우
          overdueData = response.data;
          console.log('data 필드로 데이터 추출:', overdueData.length, '건');
        } else if (response.overdue_list && Array.isArray(response.overdue_list)) {
          // overdue_list 필드가 있는 경우
          overdueData = response.overdue_list;
          console.log('overdue_list 필드로 데이터 추출:', overdueData.length, '건');
        } else {
          console.warn('알 수 없는 응답 구조:', response);
        }
      }
      
      console.log('연체관리 데이터 추출 결과:', overdueData.length, '건');
      if (overdueData.length > 0) {
        console.log('첫 번째 데이터 샘플:', overdueData[0]);
      }
      setOverdueList(overdueData);
    } catch (error) {
      console.error('연체관리 목록 조회 실패:', error);
      console.error('오류 상세:', error.response?.data);
      console.error('오류 상태:', error.response?.status);
      console.error('오류 메시지:', error.message);
      setOverdueList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyOwners = async () => {
    try {
      console.log('임대사업자 목록 조회 시작');
      const response = await api.get('/rmc/property-owners');
      console.log('임대사업자 목록 응답:', response);
      console.log('임대사업자 목록 응답 타입:', typeof response);
      console.log('임대사업자 목록 응답이 배열인가?', Array.isArray(response));
      
      // API 응답 구조에 맞게 데이터 추출
      // api.js 인터셉터가 response.data를 반환하므로, /rmc/property-owners는 배열을 직접 반환
      let ownersData = [];
      
      if (response) {
        if (response.success && response.data) {
          // 표준 응답 구조: {success: true, data: [...]}
          ownersData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          // 배열이 직접 반환된 경우 (일반적인 경우)
          ownersData = response;
        } else if (response.data && Array.isArray(response.data)) {
          // data 필드가 배열인 경우
          ownersData = response.data;
        }
      }
      
      console.log('임대사업자 목록 추출 결과:', ownersData.length, '건');
      if (ownersData.length > 0) {
        console.log('첫 번째 임대사업자 샘플:', ownersData[0]);
      }
      setPropertyOwners(ownersData);
    } catch (error) {
      console.error('임대사업자 목록 조회 실패:', error);
      console.error('오류 상세:', error.response?.data);
      setPropertyOwners([]);
    }
  };

  const fetchBuildings = async () => {
    try {
      console.log('건물 목록 조회 시작');
      const response = await api.get('/buildings');
      console.log('건물 목록 응답:', response);
      setBuildings(response.data || []);
    } catch (error) {
      console.error('건물 목록 조회 실패:', error);
      console.error('오류 상세:', error.response?.data);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleViewDetail = async (overdueId) => {
    console.log('🔍 상세보기 버튼 클릭됨:', overdueId);
    try {
      console.log('🔍 API 호출 시작:', `/overdue/${overdueId}`);
      const response = await api.get(`/overdue/${overdueId}`);
      console.log('🔍 API 응답 전체:', response);
      console.log('🔍 API 응답 데이터:', response.data);
      console.log('🔍 API 응답 데이터 타입:', typeof response.data);
      console.log('🔍 API 응답 데이터 키들:', Object.keys(response.data || {}));
      
      // 응답 데이터 구조 확인
      const overdueData = response.data.data || response.data;
      console.log('🔍 설정할 연체 데이터:', overdueData);
      console.log('🔍 연체 데이터 타입:', typeof overdueData);
      
      setSelectedOverdue(overdueData);
      setShowDetailModal(true);
      console.log('🔍 모달 표시 설정 완료');
    } catch (error) {
      console.error('❌ 연체관리 상세 조회 실패:', error);
    }
  };

  const handleSendNotification = async () => {
    try {
      if (notificationData.use_template && notificationData.selected_template) {
        // 템플릿 사용
        const messageType = notificationData.notification_type.toLowerCase();
        await api.post('/overdue/send-notification', {
          overdue_id: selectedOverdue.id,
          template_id: notificationData.selected_template,
          message_type: messageType
        });
      } else {
        // 직접 입력
        await api.post('/overdue/send-notification', {
          overdue_id: selectedOverdue.id,
          notification_type: notificationData.notification_type,
          custom_message: notificationData.custom_message
        });
      }
      
      alert('알림이 발송되었습니다.');
      setShowNotificationModal(false);
      fetchOverdueList();
    } catch (error) {
      console.error('알림 발송 실패:', error);
      alert('알림 발송에 실패했습니다.');
    }
  };

  const handleResolveOverdue = async () => {
    try {
      console.log('🔍 연체 해결 처리 시작');
      console.log('🔍 selectedOverdue:', selectedOverdue);
      console.log('🔍 resolveData:', resolveData);
      
      const requestData = {
        overdue_id: selectedOverdue.id,
        resolution_method: resolveData.resolution_method,
        notes: resolveData.notes
      };

      console.log('🔍 요청 데이터:', requestData);
      const response = await api.post('/overdue/resolve', requestData);
      console.log('🔍 응답 데이터:', response.data);
      console.log('🔍 응답 전체:', response);
      console.log('🔍 응답 상태:', response.status);
      
      if (response.data && response.data.success) {
        alert('연체가 해결 처리되었습니다.');
        setShowResolveModal(false);
        setResolveData({
          resolution_method: '',
          notes: ''
        });
        fetchOverdueList();
      } else {
        console.error('🔍 API 응답 실패:', response.data);
        console.error('🔍 응답 데이터 타입:', typeof response.data);
        console.error('🔍 응답 데이터 키들:', Object.keys(response.data || {}));
        alert('연체 해결 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('🔍 연체 해결 처리 실패:', error);
      console.error('🔍 에러 응답:', error.response?.data);
      alert('연체 해결 처리에 실패했습니다.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-4 text-sm text-gray-600">연체관리 데이터를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Clock className="w-6 h-6 text-primary-600 mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">연체관리</h1>
        </div>
        <p className="text-gray-600">세대별 연체내역을 관리하고 단계별 독촉을 진행하세요.</p>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className={`grid grid-cols-1 md:grid-cols-${(user?.role === 'rental_management_company' || 
                                                          user?.role === 'rmc_admin' || 
                                                          user?.role === 'rmc_staff' ||
                                                          (user?.user_type === 'rmc_user' && user?.role === 'admin')) ? '5' : '4'} gap-4`}>
          {(user?.role === 'rental_management_company' || 
            user?.role === 'rmc_admin' || 
            user?.role === 'rmc_staff' ||
            (user?.user_type === 'rmc_user' && user?.role === 'admin')) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자</label>
              <select
                value={filters.property_owner_id}
                onChange={(e) => handleFilterChange('property_owner_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">전체</option>
                {propertyOwners.map(owner => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">건물</label>
            <select
              value={filters.building_id}
              onChange={(e) => handleFilterChange('building_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">전체</option>
              {buildings.map(building => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">연체상태</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">전체</option>
              <option value="green">납부안내</option>
              <option value="yellow">1차독촉</option>
              <option value="brown">2차독촉</option>
              <option value="dark_brown">연체료부과</option>
              <option value="pink">내용증명</option>
              <option value="red">최종통지</option>
              <option value="black">법무단계</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">연체단계</label>
            <select
              value={filters.stage}
              onChange={(e) => handleFilterChange('stage', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">전체</option>
              <option value="DUE_REMINDER">납부안내</option>
              <option value="D3">1차독촉</option>
              <option value="D15">2차독촉</option>
              <option value="D30">연체료부과</option>
              <option value="D60">내용증명</option>
              <option value="D90">최종통지</option>
              <option value="D100">법무단계</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">검색</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="임차인명, 세대번호 검색"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 연체관리 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">연체관리 목록</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {(user?.role === 'rental_management_company' || 
                  user?.role === 'rmc_admin' || 
                  user?.role === 'rmc_staff' ||
                  (user?.user_type === 'rmc_user' && user?.role === 'admin')) && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    임대사업자
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  건물/세대
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  임차인
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  납부기한
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연체일수
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연체금액
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연체단계
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  마지막알림
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  액션
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {overdueList.map((overdue) => (
                <tr key={overdue.id} className="hover:bg-gray-50">
                  {(user?.role === 'rental_management_company' || 
                    user?.role === 'rmc_admin' || 
                    user?.role === 'rmc_staff' ||
                    (user?.user_type === 'rmc_user' && user?.role === 'admin')) && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {overdue.property_owner_name || '-'}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {overdue.building_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {overdue.unit_number}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {overdue.tenant_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {overdue.tenant_phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(overdue.due_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      overdue.overdue_days > 30 ? 'bg-red-100 text-red-800' :
                      overdue.overdue_days > 15 ? 'bg-orange-100 text-orange-800' :
                      overdue.overdue_days > 3 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {overdue.overdue_days}일
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(overdue.overdue_amount)}원
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[overdue.overdue_status]}`}>
                      {stageNames[overdue.overdue_stage]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {overdue.last_notification_date ? formatDate(overdue.last_notification_date) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🔍 상세보기 버튼 클릭 이벤트 발생:', overdue.id);
                          handleViewDetail(overdue.id);
                        }}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded hover:bg-gray-100"
                        title="상세보기"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedOverdue(overdue);
                          setShowNotificationModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          console.log('🔍 해결처리 모달 열기 버튼 클릭됨:', overdue.id);
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedOverdue(overdue);
                          setShowResolveModal(true);
                        }}
                        className="text-green-600 hover:text-green-900"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {overdueList.length === 0 && !loading && (
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">연체 내역이 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">현재 연체된 청구서가 없습니다.</p>
            <div className="mt-4 text-xs text-gray-400">
              <p>디버그 정보:</p>
              <p>로딩 상태: {loading ? '로딩 중' : '완료'}</p>
              <p>데이터 개수: {overdueList.length}</p>
            </div>
          </div>
        )}
      </div>

      {/* 상세 모달 */}
      {console.log('🔍 모달 렌더링 상태:', { showDetailModal, selectedOverdue: !!selectedOverdue, selectedOverdueData: selectedOverdue })}
      {showDetailModal && selectedOverdue && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">연체관리 상세</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">닫기</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">건물명</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedOverdue.building_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">세대번호</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedOverdue.unit_number}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">임차인명</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedOverdue.tenant_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">연락처</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedOverdue.tenant_phone}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">납부기한</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedOverdue.due_date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">연체일수</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedOverdue.overdue_days}일</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">연체금액</label>
                    <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedOverdue.overdue_amount)}원</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">연체단계</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[selectedOverdue.overdue_status]}`}>
                      {stageNames[selectedOverdue.overdue_stage]}
                    </span>
                  </div>
                </div>
                
                {selectedOverdue.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">메모</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedOverdue.notes}</p>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 알림 발송 모달 */}
      {showNotificationModal && selectedOverdue && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">알림 발송</h3>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">닫기</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">발송 대상</label>
                  <p className="text-sm text-gray-900">{selectedOverdue.tenant_name} ({selectedOverdue.tenant_phone})</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">발송 방법</label>
                  <select
                    value={notificationData.notification_type}
                    onChange={(e) => setNotificationData(prev => ({ ...prev, notification_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="SMS">SMS</option>
                    <option value="EMAIL">이메일</option>
                    <option value="BOTH">SMS + 이메일</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메시지 템플릿</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="use_template"
                        name="message_type"
                        checked={notificationData.use_template}
                        onChange={() => setNotificationData(prev => ({ ...prev, use_template: true }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <label htmlFor="use_template" className="text-sm font-medium text-gray-700">
                        템플릿 사용
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="custom_message"
                        name="message_type"
                        checked={!notificationData.use_template}
                        onChange={() => setNotificationData(prev => ({ ...prev, use_template: false }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <label htmlFor="custom_message" className="text-sm font-medium text-gray-700">
                        직접 입력
                      </label>
                    </div>
                  </div>
                </div>
                
                {notificationData.use_template && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      메시지 템플릿 선택
                      <span className="text-xs text-gray-500 ml-2">
                        ({selectedOverdue.overdue_stage} - {notificationData.notification_type})
                      </span>
                    </label>
                    {loadingTemplates ? (
                      <div className="text-sm text-gray-500">템플릿 로딩 중...</div>
                    ) : (
                      <div className="space-y-3">
                        {/* 디버깅 정보 */}
                        {process.env.NODE_ENV === 'development' && (
                          <div className="text-xs text-gray-400 mb-2">
                            템플릿 개수: {Object.keys(templates).length}개 단계
                            {selectedOverdue && (
                              <span className="ml-2">
                                현재 단계: {selectedOverdue.overdue_stage}
                              </span>
                            )}
                          </div>
                        )}
                        {/* 추천 템플릿 */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-center mb-2">
                            <span className="text-sm font-medium text-blue-800">💡 추천 템플릿</span>
                            <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                              {selectedOverdue.overdue_stage} 단계
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              const stage = selectedOverdue.overdue_stage;
                              const messageType = notificationData.notification_type.toLowerCase();
                              const template = templates[stage]?.[messageType];
                              if (template) {
                                const preview = replaceTemplateVariables(template.content, selectedOverdue);
                                setNotificationData(prev => ({ 
                                  ...prev, 
                                  selected_template: 'recommended',
                                  custom_message: preview 
                                }));
                              }
                            }}
                            className="w-full text-left p-2 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                          >
                            <div className="text-sm font-medium text-gray-900">
                              {getRecommendedTemplateTitle(selectedOverdue.overdue_stage)}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              {getRecommendedTemplateDescription(selectedOverdue.overdue_stage)}
                            </div>
                          </button>
                        </div>

                        {/* 기타 템플릿 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">다른 템플릿 선택</label>
                          <select
                            value={notificationData.selected_template}
                            onChange={(e) => {
                              const templateId = e.target.value;
                              setNotificationData(prev => ({ ...prev, selected_template: templateId }));
                              
                              if (templateId && templateId !== 'recommended') {
                                // 모든 단계에서 템플릿 찾기
                                let foundTemplate = null;
                                for (const stage in templates) {
                                  const messageType = notificationData.notification_type.toLowerCase();
                                  if (templates[stage]?.[messageType]?.id == templateId) {
                                    foundTemplate = templates[stage][messageType];
                                    break;
                                  }
                                }
                                
                                if (foundTemplate) {
                                  const preview = replaceTemplateVariables(foundTemplate.content, selectedOverdue);
                                  setNotificationData(prev => ({ ...prev, custom_message: preview }));
                                }
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">템플릿을 선택하세요</option>
                            {Object.keys(templates).length > 0 ? (
                              Object.keys(templates).map((stage) => {
                                const messageType = notificationData.notification_type.toLowerCase();
                                const template = templates[stage]?.[messageType];
                                if (template) {
                                  return (
                                    <option key={template.id} value={template.id}>
                                      [{stageNames[stage] || stage}] {template.template_name} ({template.template_type.toUpperCase()})
                                    </option>
                                  );
                                }
                                return null;
                              })
                            ) : (
                              <option value="" disabled>템플릿이 없습니다</option>
                            )}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메시지 내용</label>
                  <div className="space-y-2">
                    {/* 메시지 미리보기 */}
                    {notificationData.custom_message && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">미리보기</span>
                          <span className="text-xs text-gray-500">
                            {notificationData.notification_type} | {notificationData.custom_message.length}자
                          </span>
                        </div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap bg-white p-2 rounded border">
                          {notificationData.custom_message}
                        </div>
                      </div>
                    )}
                    
                    {/* 메시지 편집 */}
                    <textarea
                      value={notificationData.custom_message}
                      onChange={(e) => setNotificationData(prev => ({ ...prev, custom_message: e.target.value }))}
                      rows={4}
                      placeholder="템플릿을 선택하거나 직접 입력하세요."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    
                    {/* 템플릿 변수 안내 */}
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">사용 가능한 변수:</span> {'{tenant_name}'}, {'{building_name}'}, {'{unit_number}'}, {'{overdue_amount}'}, {'{overdue_days}'}, {'{due_date}'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={handleSendNotification}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
                >
                  발송
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 연체 해결 모달 */}
      {showResolveModal && selectedOverdue && (
        console.log('🔍 해결처리 모달 렌더링:', { showResolveModal, selectedOverdue: selectedOverdue?.id }),
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">연체 해결 처리</h3>
                <button
                  onClick={() => setShowResolveModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">닫기</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* 연체 정보 요약 */}
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <h4 className="font-medium text-gray-900 mb-2">연체 정보</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">임차인:</span>
                    <span className="ml-2 font-medium">{selectedOverdue.tenant?.name || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">건물:</span>
                    <span className="ml-2 font-medium">{selectedOverdue.building?.name || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">연체금액:</span>
                    <span className="ml-2 font-medium text-red-600">{formatCurrency(selectedOverdue.overdue_amount)}원</span>
                  </div>
                  <div>
                    <span className="text-gray-600">연체일수:</span>
                    <span className="ml-2 font-medium">{selectedOverdue.overdue_days}일</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">해결 방법</label>
                  <select
                    value={resolveData.resolution_method}
                    onChange={(e) => setResolveData(prev => ({ ...prev, resolution_method: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">선택하세요</option>
                    <option value="full_payment">전액 납부</option>
                    <option value="partial_payment">부분 납부</option>
                    <option value="payment_plan">납부 계획 수립</option>
                    <option value="contract_termination">계약 해지</option>
                    <option value="other">기타</option>
                  </select>
                </div>

                {/* 입금처리 섹션 */}
                {/* 안내 메시지 */}
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        입금처리는 별도로 진행해주세요
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>연체 해결처리는 연체관리 상태만 변경합니다. 실제 입금처리는 <strong>입금관리</strong>에서 별도로 진행해주세요.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea
                    value={resolveData.notes}
                    onChange={(e) => setResolveData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    placeholder="해결 과정에 대한 메모를 입력하세요."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowResolveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={(e) => {
                    console.log('🔍 해결처리 버튼 클릭됨');
                    e.preventDefault();
                    e.stopPropagation();
                    handleResolveOverdue();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  해결 처리
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverdueManagement;
